// TooltipContext.tsx
import {
  createContext,
  type CSSProperties,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import { getQuadrant } from '@/utils/dom';

import TooltipRenderer from './TooltipRenderer';
import type { TooltipContextValue, TooltipState } from './type';

const TooltipContext = createContext<TooltipContextValue | null>(null);

export const TooltipProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [tooltipState, setTooltipState] = useState<TooltipState>({
    tooltipNode: null,
    style: { top: 0, left: 0 },
    visible: false,
  });

  const offsetY = 8;
  const offsetX = 0;

  const showTooltip = useCallback(
    async (
      content: ReactNode,
      anchor: HTMLElement,
      shortcut?: string,
      offset: {
        x: number;
        y: number;
      } = {
        x: 0,
        y: 0,
      },
      style?: CSSProperties,
    ) => {
      const rect = anchor.getBoundingClientRect();

      const quadrant = getQuadrant(
        {
          width: window.document.documentElement.clientWidth,
          height: window.document.documentElement.clientHeight,
        },
        {
          top: rect.top,
          left: rect.left,
        },
      );

      let pos: CSSProperties = {
        top: rect.bottom + window.scrollY + offsetY + offset.y,
        right:
          document.documentElement.clientWidth -
          rect.right +
          window.scrollX +
          offsetX +
          offset.x,
      };
      switch (quadrant) {
        case 2: {
          pos = {
            top: rect.bottom + window.scrollY + offsetY + offset.y,
            left: rect.left + window.scrollX + offsetX + offset.x,
          };
          break;
        }
        case 3: {
          pos = {
            bottom:
              document.documentElement.clientHeight -
              rect.top +
              offsetY +
              offset.y,
            left: rect.left + window.scrollX + offsetX + offset.x,
          };
          break;
        }
        case 4: {
          pos = {
            bottom:
              document.documentElement.clientHeight -
              rect.top +
              offsetY +
              offset.y,
            right:
              document.documentElement.clientWidth -
              rect.right +
              window.scrollX +
              offsetX +
              offset.x,
          };
          break;
        }
        default:
          break;
      }

      setTooltipState({
        tooltipNode: content,
        style: {
          ...style,
          ...pos,
        },
        visible: true,
        shortcut,
      });
    },
    [],
  );

  const hideTooltip = useCallback(() => {
    setTooltipState((prev) => ({ ...prev, visible: false }));
  }, []);

  return (
    <TooltipContext.Provider value={{ showTooltip, hideTooltip }}>
      {children}
      <TooltipRenderer {...tooltipState} />
    </TooltipContext.Provider>
  );
};

export const useTooltip = (): TooltipContextValue => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltip must be used within a TooltipProvider');
  }
  return context;
};
